import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  FormControl,
  Input,
  Button,
  InputGroup,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  Avatar,
  InputRightElement,
  Image,
  Switch,
} from "@chakra-ui/react";

import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { InputControl, RadioControl, SelectControl } from "./formControls";
import NonAvatar from "../Icons/avartar";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import vi from "date-fns/locale/vi";
import { useCategories } from "../../categorys/formAndPopup/formEmployee";
import { employeeCall } from "../../api/employee";
import { DataPlaceOfIdentification } from "../../utils/globalVariable";
import toast from "react-hot-toast";
import { commonCall } from "../../api/common";
import imageIcon from "../../assets/images/booking/image.svg";
import { RiResetLeftFill } from "react-icons/ri";
import { settingCall } from "../../api/settings";
import { DataSetting } from "../../utils/contants";

registerLocale("vi", vi);
const baseURL = process.env.REACT_APP_BACKEND_API || "http://localhost:3001";

// Ô ảnh VNeID (mặt trước / mặt sau), không bắt buộc, giao diện giống ô ảnh CCCD.
// Chọn ảnh là tự upload, upload lỗi thì hiện nút "Tải lại".
const VneidImageField = ({ label, name, defaultPath, isOpen, setValue }) => {
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && defaultPath) setImage({ path: defaultPath });
  }, [isOpen, defaultPath]);

  const upload = async (file) => {
    setIsLoading(true);
    try {
      const { filePath } = await commonCall.uploadFile(
        file,
        "salesContractImage",
      );
      if (filePath) {
        setImage({ path: filePath });
        setValue(name, filePath);
        toast.success("Upload ảnh thành công");
      } else {
        toast.error("Có lỗi xảy ra khi upload ảnh");
      }
    } catch (error) {
      // commonCall.uploadFile đã báo lỗi bằng toast, bấm Tải lại để thử lại
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImage({ imageUrl: URL.createObjectURL(file), file });
    upload(file);
  };

  return (
    <Box flex={1} mb={"24px"}>
      <Text color={"var(--color-label-employee)"} fontSize={"12px"}>
        {label}
      </Text>
      <Flex
        mt={"8px"}
        border={"0.5px solid var(--bg-line-employee)"}
        borderRadius={"8px"}
        justifyContent={"center"}
        position={"relative"}
      >
        <Box
          overflow={"hidden"}
          w={"100%"}
          h={"300px"}
          display={"block"}
          borderRadius={"11px"}
          position={"relative"}
        >
          {(image?.imageUrl || image?.path) && (
            <Image
              src={image?.imageUrl || `${baseURL}/${image?.path}`}
              objectFit={"contain"}
              h={"100%"}
              w={"100%"}
            />
          )}
        </Box>
        {(image?.imageUrl || image?.path) && (
          <Box position="absolute" top="16px" right="16px" zIndex={2}>
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => {
                setImage(null);
                setValue(name, null);
              }}
              padding={2}
            >
              Xóa
            </Button>
          </Box>
        )}
        <Flex
          position={"absolute"}
          right={"8px"}
          bottom={"8px"}
          cursor={"pointer"}
        >
          <Box
            padding={"8px"}
            bg={"var(--fill-avatar)"}
            borderRadius={"50%"}
            mr={"8px"}
            position={"relative"}
          >
            <Input
              type="file"
              accept="image/*"
              opacity={0}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              cursor="pointer"
              onChange={onChangeFile}
            />
            <Image
              src={imageIcon}
              _hover={{
                filter: "var(--filter-red)",
              }}
            />
          </Box>
        </Flex>
      </Flex>
      {image?.file && (
        <Flex w={"full"} justifyContent={"flex-end"} mt={2}>
          <Button
            bg={"var(--linear-gradient-employee)"}
            color={"var(--color-main)"}
            fontSize={"12px"}
            lineHeight={"17px"}
            fontWeight={"600"}
            _hover={{
              filter: "brightness(90%)",
            }}
            onClick={() => upload(image.file)}
            h={"28px"}
            minH={"28px"}
            w={"90px"}
            isLoading={isLoading}
          >
            Tải lại
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export const FormEmployee = memo(
  ({ isOpen, onClose, department, defaultValues, onSubmit, showInfo }) => {
    // Người giới thiệu chỉ super admin được sửa. Hành chính nhân sự sửa được
    // thông tin nhân viên nhưng không đổi được người giới thiệu (BE cũng chặn
    // bằng quyền employee:updateReferralEmployee).
    const currentUser = useSelector((state) => state.auth.login?.currentUser);
    const canUpdateReferralCode = (currentUser?.roles || []).includes("ALL");

    const schema = Yup.object().shape({
      fullName: Yup.string().required("Vui lòng nhập họ tên"),
      code: Yup.string().required("Mã nhân viên là bắt buộc"),
      // .test("is-unique", "Mã nhân viên đã tồn tại", async (value) => {
      //   if (!value) return true;
      //   return await employeeCall.checkCodeUnique(value);
      // }),
      password: Yup.string()
        .required("Mật khẩu là bắt buộc")
        .matches(
          /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%&*])[a-zA-Z0-9!@#$%&*]+$/,
          "Mật khẩu phải có ít nhất 1 chữ hoa, 1 chữ thường, 1 số, 1 ký tự đặc biệt, và không chứa dấu.",
        ),
      phone: Yup.string()
        .required("Vui lòng nhập số điện thoại")
        .matches(/^[0-9]{10,11}$/, "Số điện thoại không hợp lệ."),
      email: Yup.string()
        .required("Vui lòng nhập email")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email không hợp lệ"),

      identificationNumber: Yup.string()
        .test(
          "is-valid-id-number",
          "Số CCCD/CMND không hợp lệ (9 hoặc 12 số).",
          (value) => {
            if (!value) {
              return true;
            }
            return /^\d{9}$|^\d{12}$/.test(value);
          },
        )
        .nullable(),
      dateOfIdentification: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian cấp không hợp lệ."),
      startTime: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian bắt đầu không hợp lệ."),
      bankAccount: Yup.string().required("Vui lòng nhập số tài khoản"),
    });
    const schema2 = Yup.object().shape({
      fullName: Yup.string().required("Vui lòng nhập họ tên"),
      code: Yup.string()
        .required("Mã nhân viên là bắt buộc")
        .test("is-unique", "Mã nhân viên đã tồn tại", async (value) => {
          if (!value) return true;
          return await employeeCall.checkCodeUnique(value);
        }),
      // referralCodeId: Yup.string()
      //   .test(
      //     "is-valid",
      //     "Mã giới thiệu phải có độ dài 24 ký tự và chỉ chứa ký tự hexadecimal",
      //     (value) => {
      //       if (!value) {
      //         return true;
      //       }
      //       return value.length === 24 && /^[a-fA-F0-9]+$/.test(value);
      //     }
      //   )
      //   .nullable(),
      password: Yup.string()
        .required("Mật khẩu là bắt buộc")
        .matches(
          /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%&*])[a-zA-Z0-9!@#$%&*]+$/,
          "Mật khẩu phải có ít nhất 1 chữ hoa, 1 chữ thường, 1 số, 1 ký tự đặc biệt, và không chứa dấu.",
        ),
      phone: Yup.string()
        .required("Vui lòng nhập số điện thoại")
        .matches(/^[0-9]{10,11}$/, "Số điện thoại không hợp lệ."),
      email: Yup.string()
        .required("Vui lòng nhập email")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email không hợp lệ"),

      identificationNumber: Yup.string()
        .test(
          "is-valid-id-number",
          "Số CCCD/CMND không hợp lệ (9 hoặc 12 số).",
          (value) => {
            if (!value) {
              return true;
            }
            return /^\d{9}$|^\d{12}$/.test(value);
          },
        )
        .nullable(),
      dateOfIdentification: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian cấp không hợp lệ."),
      startTime: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian bắt đầu không hợp lệ."),
      bankAccount: Yup.string().required("Vui lòng nhập số tài khoản"),
    });
    const schemaEdit = Yup.object().shape({
      fullName: Yup.string().required("Vui lòng nhập họ tên"),
      code: Yup.string()
        .required("Mã nhân viên là bắt buộc")
        .test("is-unique", "Mã nhân viên đã tồn tại", async (value) => {
          if (!value) return true;
          // Giữ nguyên mã cũ của chính nhân viên đang sửa
          if (value === defaultValues?.code) return true;
          return await employeeCall.checkCodeUnique(value);
        }),
      // referralCodeId: Yup.string()
      //   .test(
      //     "is-valid",
      //     "Mã giới thiệu phải có độ dài 24 ký tự và chỉ chứa ký tự hexadecimal",
      //     (value) => {
      //       if (!value) {
      //         return true;
      //       }
      //       return value.length === 24 && /^[a-fA-F0-9]+$/.test(value);
      //     }
      //   )
      //   .nullable(),
      phone: Yup.string()
        .required("Vui lòng nhập số điện thoại")
        .matches(/^[0-9]{10,11}$/, "Số điện thoại không hợp lệ."),
      email: Yup.string()
        .required("Vui lòng nhập email")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email không hợp lệ"),
      identificationNumber: Yup.string()
        .test(
          "is-valid-id-number",
          "Số CCCD/CMND không hợp lệ (9 hoặc 12 số).",
          (value) => {
            if (!value) {
              return true;
            }
            return /^\d{9}$|^\d{12}$/.test(value);
          },
        )
        .nullable(),
      dateOfIdentification: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian cấp không hợp lệ."),
      startTime: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian bắt đầu không hợp lệ."),
      bankAccount: Yup.string().required("Vui lòng nhập số tài khoản"),
    });

    const [isLoadingUpload, setIsloadingUpload] = useState(false);
    const [idCardFrontImage, setIdCardFrontImage] = useState(null);
    const [idCardBackImage, setIdCardBackImage] = useState(null);
    // const [latestEmployeeId, setLatestEmployeeId] = useState(null);

    const {
      register,
      getValues,
      handleSubmit,
      setValue,
      clearErrors,
      setError,
      watch,
      reset,
      formState: { errors },
    } = useForm({
      resolver: defaultValues
        ? yupResolver(schemaEdit)
        : department === 0
          ? yupResolver(schema)
          : yupResolver(schema2),

      defaultValues: { ...defaultValues, department } || {},
    });

    const [shouldFetch, setShouldFetch] = useState(false);
    const [checkedSwitch, setCheckedSwitch] = useState(
      defaultValues?.isBrokerDebtCertificate || false,
    );
    useEffect(() => {
      const timer = setTimeout(() => {
        setShouldFetch(true);
      }, 1200);

      return () => clearTimeout(timer);
    }, []);

    const { constantData } = useCategories(
      watch("saleAreaId"),
      null,
      null,
      shouldFetch,
    );

    const formatEntriesForSelect = (entries) =>
      Object.entries(entries || {}).map(([key, value]) => ({
        value: key,
        label: value,
      }));

    const genders = [
      {
        value: 0,
        label: "Nam",
      },
      {
        value: 1,
        label: "Nữ",
      },
      {
        value: 2,
        label: "Khác",
      },
    ];

    const jobPositionSupportBussines = useMemo(
      () => formatEntriesForSelect(constantData?.jobPositionSupportBussines),
      [constantData?.jobPositionSupportBussines],
    );
    const jobPositionBussines = useMemo(
      () => formatEntriesForSelect(constantData?.jobPositionBussines),
      [constantData?.jobPositionBussines],
    );
    const jobPositionAdminsData = useMemo(
      () => formatEntriesForSelect(constantData?.jobPositionAdminsData),
      [constantData?.jobPositionAdminsData],
    );
    const supportDepartment = useMemo(
      () => formatEntriesForSelect(constantData?.supportDepartments),
      [constantData?.supportDepartments],
    );
    const jobTitles = useMemo(
      () => formatEntriesForSelect(constantData?.jobTitles),
      [constantData?.jobTitles],
    );
    const branches = useMemo(
      () => formatEntriesForSelect(constantData?.branches),
      [constantData?.branches],
    );
    const departments = useMemo(
      () => formatEntriesForSelect(constantData?.departments),
      [constantData?.departments],
    );
    const businessCategorys = useMemo(
      () => formatEntriesForSelect(constantData?.businessCategorys),
      [constantData?.businessCategorys],
    );
    const guaranteeTypes = useMemo(
      () => formatEntriesForSelect(constantData?.guaranteeTypes),
      [constantData?.guaranteeTypes],
    );
    const saleAreas = useMemo(
      () => formatEntriesForSelect(constantData?.saleAreas),
      [constantData?.saleAreas],
    );
    const saleBlocks = useMemo(
      () => formatEntriesForSelect(constantData?.saleBlocks),
      [constantData?.saleBlocks],
    );

    const getDataLatestEmployeeId = async () => {
      const toastData = toast.loading("Đang lấy mã nhân viên mới nhất ...");
      try {
        const placeCheckin = await settingCall.get(
          DataSetting.latest_employee_id,
        );

        if (placeCheckin?.items?.value) {
          if (Number(department) === 1 || Number(department) === 3)
            setValue("code", `CTV00${Number(placeCheckin?.items?.value) + 1}`);
          else if (Number(department) === 0)
            setValue("code", `MSH00${Number(placeCheckin?.items?.value) + 1}`);
        }

        toast.success("Lấy thành công");
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.dismiss(toastData);
      }
      toast.dismiss(toastData);
    };

    useEffect(() => {
      if (isOpen) {
        if (Number(department) === 2) {
          reset({
            ...defaultValues,
            jobPositionId: jobPositionAdminsData[0]?.value || "",
            department,
          });
        } else {
          reset({
            ...defaultValues,
            department,
          });
        }
        setAvatar(null);
      }

      if (defaultValues?.idCardFrontImage) {
        setIdCardFrontImage((prev) => ({
          ...prev,
          path: defaultValues?.idCardFrontImage,
          id: 1,
          status: true,
          init: true,
        }));
      }

      if (defaultValues?.idCardBackImage) {
        setIdCardBackImage((prev) => ({
          ...prev,
          path: defaultValues?.idCardBackImage,
          id: 2,
          status: true,
          init: true,
        }));
      }

      if (!defaultValues?.id) {
        const fetchDataLatestEmployeeId = async () => {
          try {
            const placeCheckin = await settingCall.get(
              DataSetting.latest_employee_id,
            );

            if (placeCheckin?.items?.value) {
              if (Number(department) === 1)
                setValue(
                  "code",
                  `CTV00${Number(placeCheckin?.items?.value) + 1}`,
                );
              else if (Number(department) === 0)
                setValue(
                  "code",
                  `MSH00${Number(placeCheckin?.items?.value) + 1}`,
                );
            }
          } catch (error) {
            console.error("Error fetching data:", error);
          }
        };
        fetchDataLatestEmployeeId();
      }
    }, [isOpen, defaultValues, reset]);

    const onChangeCode = (e) => {
      setValue("code", e.target.value);
    };
    const onchangeJobPosition = (option) => {
      setValue("jobPositionId", option);
    };

    const onchangeJobTitle = (option) => {
      setValue("jobTitleId", option);
    };
    const onchangeSupportDepartment = (option) => {
      setValue("supportDepartmentId", option);
    };

    const onchangeBranch = (option) => {
      setValue("branchId", option);
    };

    const onchangeGender = (option) => {
      setValue("gender", option);
    };

    const onchangeBusinessCategorys = (option) => {
      setValue("businessCategoryId", option);
    };

    const onchangeGuaranteeTypes = (option) => {
      setValue("guaranteeTypeId", option);
    };

    // const onchangeDepartments = (option) => {
    //   setValue("department", Number(option));
    // };

    const onchangeSaleAreas = (option) => {
      clearErrors("saleBlockId");
      setValue("saleAreaId", option);
    };

    const onchangeSaleBlocks = (option) => {
      if (!watch("saleAreaId")) {
        setError("saleBlockId", {
          type: "manual",
          message: "Bạn phải chọn vùng trước khi chọn khối",
        });
        return;
      }
      setValue("saleBlockId", option);
    };

    const uploadIdCardFrontImage = async (file) => {
      setIsloadingUpload(true);
      try {
        if (file) {
          const { filePath } = await commonCall.uploadFile(
            file,
            "salesContractImage",
          );

          if (filePath) {
            setIdCardFrontImage({
              path: filePath,
              id: Date.now(),
              status: true,
              init: false,
            });
            clearErrors("idCardFrontImage");
            setValue("idCardFrontImage", filePath);
            toast.success("Upload ảnh thành công");
          } else {
            toast.error("Có lỗi xảy ra khi upload ảnh");
          }
        }
      } catch (error) {
        // commonCall.uploadFile đã báo lỗi bằng toast, bấm Tải lại để thử lại
        console.error(error);
      } finally {
        setIsloadingUpload(false);
      }
    };

    const changeIdCardFrontImage = (event, type, id) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        const imageUrl = URL.createObjectURL(file);

        reader.onload = (e) => {
          setIdCardFrontImage((prev) => ({
            ...prev,
            id: id || Date.now(),
            imageUrl: imageUrl,
            file: file,
            status: false,
          }));
          clearErrors("idCardFrontImage");
          // Chọn ảnh là tự upload, không cần bấm nút
          uploadIdCardFrontImage(file);
        };

        reader.readAsDataURL(file);
      }
    };

    const uploadIdCardBackImage = async (file) => {
      setIsloadingUpload(true);
      try {
        if (file) {
          const { filePath } = await commonCall.uploadFile(
            file,
            "salesContractImage",
          );

          if (filePath) {
            setIdCardBackImage({
              path: filePath,
              id: Date.now(),
              status: true,
              init: false,
            });
            clearErrors("idCardBackImage");
            setValue("idCardBackImage", filePath);
            toast.success("Upload ảnh thành công");
          } else {
            toast.error("Có lỗi xảy ra khi upload ảnh");
          }
        }
      } catch (error) {
        // commonCall.uploadFile đã báo lỗi bằng toast, bấm Tải lại để thử lại
        console.error(error);
      } finally {
        setIsloadingUpload(false);
      }
    };

    const changeIdCardBackImage = (event, type, id) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        const imageUrl = URL.createObjectURL(file);

        reader.onload = (e) => {
          setIdCardBackImage((prev) => ({
            ...prev,
            id: id || Date.now(),
            imageUrl: imageUrl,
            file: file,
            status: false,
          }));
          clearErrors("idCardBackImage");
          // Chọn ảnh là tự upload, không cần bấm nút
          uploadIdCardBackImage(file);
        };

        reader.readAsDataURL(file);
      }
    };

    const status = [
      { value: 0, label: "Đang làm việc" },
      { value: 1, label: "Dừng làm việc" },
    ];
    const [zIndex, setZIndex] = useState("");
    const [focusStates, setFocusStates] = useState({});
    // eslint-disable-next-line
    const [disableInput, setDisableInput] = useState(true);
    // const [inputColors, setInputColors] = useState({
    //   startTime: "var(--color-text-unclick)",
    //   endTime: "var(--color-text-unclick)",
    //   guaranteeDate: "var(--color-text-unclick)",
    //   dateOfIdentification: "var(--color-text-unclick)",
    //   depositDate: "var(--color-text-unclick)",
    // });

    const handleFocus = (field, data) => {
      if (data) {
        setZIndex(data);
      }
      setFocusStates((prev) => ({ ...prev, [field]: true }));
    };

    const handleBlur = (field, data) => {
      setZIndex("");
      setFocusStates((prev) => ({ ...prev, [field]: false }));
    };

    const fileAvatarInputRef = useRef(null);
    const [avatar, setAvatar] = useState(null);

    const handleImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageDataUrl = e.target.result;
          setAvatar(imageDataUrl);
          setValue("file", file);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleClick = () => {
      if (fileAvatarInputRef.current) {
        fileAvatarInputRef.current.click();
      }
    };
    const handleDateChange = (selectedDate, inputName) => {
      setValue(inputName, selectedDate, { shouldValidate: true });
      // setInputColors((prevColors) => ({
      //   ...prevColors,
      //   [inputName]: selectedDate
      //     ? "var(--color-info-employee)"
      //     : "var(--color-text-unclick)",
      // }));
    };

    const handleActionStateChange = (option) => {
      setValue("status", option);
    };

    const submitHandler = (data) => {
      delete data.remainingLeave;
      // Mã giới thiệu chỉ sinh 1 lần ở backend, không được gửi lên khi sửa
      delete data.affiliateCode;
      onSubmit(data);
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        // motionPreset="fade"
      >
        <ModalOverlay />
        <ModalContent
          backgroundColor="var(--color-main)"
          borderRadius={"16px"}
          padding={"24px"}
          h={"86.5vh"}
          w={"100%"}
          maxW={"625px"}
          overflow="auto"
          // sx={{
          //   "&::-webkit-scrollbar": {
          //     width: "4px",
          //   },
          //   "&::-webkit-scrollbar-thumb": {
          //     width: "4px",
          //     borderRadius: "50px",
          //     background: "var(--color-background)",
          //   },
          //   "&::-moz-scrollbar": {
          //     width: "4px",
          //   },
          //   "&::-moz-scrollbar-thumb": {
          //     width: "4px",
          //     borderRadius: "50px",
          //     background: "var(--color-background)",
          //   },
          // }}
        >
          <form onSubmit={handleSubmit(submitHandler)}>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"10px"}
            >
              <Text
                color="var(--color-header-employee)"
                fontSize={"18px"}
                fontWeight={"600"}
              >
                {showInfo
                  ? "Thông tin nhân viên"
                  : Boolean(department)
                    ? defaultValues
                      ? "Sửa thông tin nhân viên kinh doanh"
                      : "Nhân viên kinh doanh mới"
                    : defaultValues
                      ? "Sửa thông tin nhân viên hỗ trợ kinh doanh"
                      : "Nhân viên hỗ trợ kinh doanh mới"}
              </Text>
              <Box cursor={"pointer"} onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="#99A4B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 9L9 15"
                    stroke="#99A4B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 9L15 15"
                    stroke="#99A4B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </Flex>
            <Box
              w={"100%"}
              h={"1px"}
              backgroundColor={"var(--bg-line-employee)"}
              marginTop={"26px"}
              marginBottom={"24px"}
            ></Box>
            <Box w={"100%"} maxW={"545px"} m={"0 auto"}>
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
              >
                <Box flex={"1"} position={"relative"}>
                  <Text
                    fontSize={"16px"}
                    fontWeight={"600"}
                    color={"var(--color-header-employee)"}
                    marginBottom={"16px"}
                  >
                    Ảnh đại diện
                  </Text>
                  <Flex
                    w={"120px"}
                    h={"120px"}
                    borderRadius={"50%"}
                    bgColor={"var(--fill-avatar)"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    onClick={handleClick}
                    cursor="pointer"
                    position={"relative"}
                  >
                    {avatar || watch("avatar") ? (
                      <Avatar
                        src={
                          avatar ||
                          `${process.env.REACT_APP_BACKEND_API}/${watch(
                            "avatar",
                          )}`
                        }
                        size="2xl"
                        name=""
                      />
                    ) : (
                      <NonAvatar />
                    )}
                  </Flex>
                  <Input
                    ref={fileAvatarInputRef}
                    position={"absolute"}
                    type="file"
                    w={"0"}
                    h={"0"}
                    border={"0"}
                    outline={"0"}
                    padding={"0"}
                    m={"0"}
                    overflow={"hidden"}
                    onChange={handleImageChange}
                    accept="image/png, image/jpeg"
                  />
                </Box>
                <Box flex={"1"}>
                  <Text
                    color={"var(--color-header-employee)"}
                    fontSize={"16px"}
                    fontWeight={"600"}
                    mb={"16px"}
                  >
                    Thông tin tài khoản
                  </Text>
                  <Box mb={"13px"}>
                    <Flex gap={"8px"} position={"relative"}>
                      <InputControl
                        label="Mã nhân viên"
                        placeholder="Nhập mã nhân viên"
                        errors={errors.code}
                        register={register("code")}
                        onFocus={() => handleFocus("code")}
                        onBlur={() => handleBlur("code")}
                        state={focusStates["code"]}
                        mwidth={"100%"}
                        isRequired={true}
                      />
                      <Flex
                        mt={"6px"}
                        gap={"8px"}
                        cursor={"pointer"}
                        onClick={getDataLatestEmployeeId}
                        flexDirection={"column"}
                        position={"absolute"}
                        right={"0px"}
                        bottom={"0px"}
                        padding={"12px"}
                      >
                        <RiResetLeftFill
                          color="var(--color-option-employee-hover)"
                          size={"18px"}
                        />
                      </Flex>
                    </Flex>

                    <Flex mt={"6px"} gap={"8px"} flexDirection={"column"}>
                      <Text
                        fontSize={"12px"}
                        color="var(--color-option-employee-hover)"
                        fontStyle={"italic"}
                      >
                        Ấn vào nút để lấy mã nhân viên mới nhất
                      </Text>
                    </Flex>
                  </Box>
                  <InputControl
                    type="password"
                    label="Mật khẩu"
                    placeholder="Đặt mật khẩu"
                    errors={errors.password}
                    register={register("password")}
                    onFocus={() => handleFocus("password")}
                    onBlur={() => handleBlur("password")}
                    state={focusStates["password"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                </Box>
              </Flex>
              {/* Khooi 1 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Thông tin cá nhân
                </Text>
                {/* 1 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    label="Họ và tên"
                    placeholder="Nhập họ và tên"
                    errors={errors.fullName}
                    register={register("fullName")}
                    onFocus={() => handleFocus("fullName")}
                    onBlur={() => handleBlur("fullName")}
                    state={focusStates["fullName"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                  <SelectControl
                    label="Chi nhánh"
                    dataOption={branches}
                    selectedMain={watch("branchId")}
                    placeholder="Chọn chi nhánh"
                    onChange={onchangeBranch}
                    onFocus={() => handleFocus("branchId")}
                    onBlur={() => handleBlur("branchId")}
                    state={focusStates["branchId"]}
                    registerProps={register("branchId")}
                    isSearchable={true}
                  />
                </Flex>
                {/* 1.2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <FormControl zIndex={zIndex === "dateOfBirth" ? 3 : 0}>
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      fontWeight={400}
                      color={
                        focusStates["dateOfBirth"]
                          ? "var(--color-option-employee-hover)"
                          : "var(--color-label-employee)"
                      }
                    >
                      Ngày sinh
                    </FormLabel>
                    <InputGroup>
                      <DatePicker
                        width="100%"
                        selected={watch("dateOfBirth")}
                        onChange={(date) =>
                          handleDateChange(date, "dateOfBirth")
                        }
                        dateFormat="dd/MM/yyyy"
                        locale="vi"
                        placeholderText="dd/mm/yy"
                        className="custom-datepicker datePickerStyle"
                        onFocus={() =>
                          handleFocus("dateOfBirth", "dateOfBirth")
                        }
                        onBlur={() => handleBlur("dateOfBirth")}
                      />
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    </InputGroup>
                    {errors.dateOfBirth && (
                      <Text fontSize={{ base: "12px" }} color={"red"}>
                        {errors.dateOfBirth.message}
                      </Text>
                    )}
                  </FormControl>
                  <SelectControl
                    label="Giới tính"
                    dataOption={genders}
                    selectedMain={watch("gender")}
                    placeholder="Chọn giới tính"
                    onChange={onchangeGender}
                    onFocus={() => handleFocus("gender")}
                    onBlur={() => handleBlur("gender")}
                    state={focusStates["gender"]}
                    registerProps={register("gender")}
                  />
                </Flex>
                {/* 2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  {Number(department) === 2 ? (
                    <SelectControl
                      label="Chức vụ"
                      dataOption={jobPositionAdminsData}
                      selectedMain={watch("jobPositionId")}
                      placeholder="Chọn chức vụ"
                      onChange={onchangeJobPosition}
                      onFocus={() => handleFocus("jobPositionId")}
                      onBlur={() => handleBlur("jobPositionId")}
                      state={focusStates["jobPositionId"]}
                      registerProps={register("jobPositionId")}
                      isSearchable={true}
                    />
                  ) : Number(department) === 1 ? (
                    <SelectControl
                      label="Chức vụ"
                      dataOption={jobPositionBussines}
                      selectedMain={watch("jobPositionId")}
                      placeholder="Chọn chức vụ"
                      onChange={onchangeJobPosition}
                      onFocus={() => handleFocus("jobPositionId")}
                      onBlur={() => handleBlur("jobPositionId")}
                      state={focusStates["jobPositionId"]}
                      registerProps={register("jobPositionId")}
                      isSearchable={true}
                    />
                  ) : (
                    <SelectControl
                      label="Chức vụ"
                      dataOption={jobPositionSupportBussines}
                      selectedMain={watch("jobPositionId")}
                      placeholder="Chọn chức vụ"
                      onChange={onchangeJobPosition}
                      onFocus={() => handleFocus("jobPositionId")}
                      onBlur={() => handleBlur("jobPositionId")}
                      state={focusStates["jobPositionId"]}
                      registerProps={register("jobPositionId")}
                      isSearchable={true}
                    />
                  )}

                  <SelectControl
                    label="Chức danh"
                    dataOption={jobTitles}
                    selectedMain={watch("jobTitleId")}
                    placeholder="Chọn chức danh"
                    onChange={onchangeJobTitle}
                    onFocus={() => handleFocus("jobTitleId")}
                    onBlur={() => handleBlur("jobTitleId")}
                    state={focusStates["jobTitleId"]}
                    registerProps={register("jobTitleId")}
                    isSearchable={true}
                  />
                </Flex>
                {!Boolean(department) && (
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    gap={"27px"}
                    mb={"12px"}
                  >
                    <SelectControl
                      label="Chức phòng ban"
                      dataOption={supportDepartment}
                      selectedMain={watch("supportDepartmentId")}
                      placeholder="Chọn phòng ban"
                      onChange={onchangeSupportDepartment}
                      onFocus={() => handleFocus("supportDepartmentId")}
                      onBlur={() => handleBlur("supportDepartmentId")}
                      state={focusStates["supportDepartmentId"]}
                      registerProps={register("supportDepartmentId")}
                      isSearchable={true}
                    />
                  </Flex>
                )}
                {/* 3 */}
                {Boolean(department) && (
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    gap={"27px"}
                  >
                    <SelectControl
                      label="Vùng"
                      dataOption={saleAreas}
                      selectedMain={watch("saleAreaId")}
                      placeholder="Chọn vùng"
                      isSearchable={true}
                      onChange={onchangeSaleAreas}
                      onFocus={() => handleFocus("saleAreaId")}
                      onBlur={() => handleBlur("saleAreaId")}
                      state={focusStates["saleAreaId"]}
                      registerProps={register("saleAreaId")}
                    />
                    <SelectControl
                      label="Khối"
                      selectedMain={watch("saleBlockId")}
                      dataOption={saleBlocks}
                      placeholder="Chọn khối"
                      errorMessage={errors.saleBlockId}
                      onChange={onchangeSaleBlocks}
                      onFocus={() => handleFocus("saleBlockId")}
                      onBlur={() => handleBlur("saleBlockId")}
                      state={focusStates["saleBlockId"]}
                      registerProps={register("saleBlockId")}
                      isSearchable={true}
                    />
                  </Flex>
                )}
              </Box>
              {/* Khooi 2 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Hoạt động
                </Text>
                {/* 1 */}
                <FormLabel
                  fontSize={{ base: "12px" }}
                  fontWeight={400}
                  color={"var(--color-label-employee)"}
                  mb={"12px"}
                >
                  Trạng thái hoạt động
                </FormLabel>

                <RadioControl
                  options={status}
                  selectedValue={watch("status")}
                  onChange={handleActionStateChange}
                />
                {/* 2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                >
                  <FormControl zIndex={zIndex === "startTime" ? 3 : 0}>
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      fontWeight={400}
                      color={
                        focusStates["startTime"]
                          ? "var(--color-option-employee-hover)"
                          : "var(--color-label-employee)"
                      }
                    >
                      Ngày vào làm
                    </FormLabel>
                    <InputGroup>
                      <DatePicker
                        width="100%"
                        selected={watch("startTime")}
                        onChange={(date) => handleDateChange(date, "startTime")}
                        dateFormat="dd/MM/yyyy"
                        locale="vi"
                        placeholderText="dd/mm/yy"
                        className="custom-datepicker datePickerStyle"
                        onFocus={() => handleFocus("startTime", "startTime")}
                        onBlur={() => handleBlur("startTime")}
                      />
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    </InputGroup>
                    {errors.startTime && (
                      <Text fontSize={{ base: "12px" }} color={"red"}>
                        {errors.startTime.message}
                      </Text>
                    )}
                  </FormControl>
                  <FormControl zIndex={zIndex === "endTime" ? 3 : 0}>
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      fontWeight={400}
                      color={
                        disableInput
                          ? "var(--bg-line-employee)"
                          : focusStates["endTime"]
                            ? "var(--color-option-employee-hover)"
                            : "var(--color-label-employee)"
                      }
                    >
                      Ngày nghỉ việc
                    </FormLabel>
                    <InputGroup>
                      <DatePicker
                        selected={watch("endTime")}
                        onChange={(date) => handleDateChange(date, "endTime")}
                        dateFormat="dd/MM/yyyy"
                        locale="vi"
                        placeholderText="dd/mm/yy"
                        className="custom-datepicker datePickerStyle colorDisableInput"
                        onFocus={() => handleFocus("endTime", "endTime")}
                        onBlur={() => handleBlur("endTime")}
                        disabled={disableInput ? true : false}
                      />
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                </Flex>
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  my={"12px"}
                >
                  <InputControl
                    label="Ngày nghỉ phép còn lại"
                    placeholder="Nhập số ngày phép"
                    errors={errors.remainingLeave}
                    register={register("remainingLeave")}
                    onFocus={() => handleFocus("remainingLeave")}
                    onBlur={() => handleBlur("remainingLeave")}
                    state={focusStates["remainingLeave"]}
                    value={watch("remainingLeave")}
                    w={"50%"}
                    disabled={true}
                    pointerEvents="none"
                  />
                </Flex>
                <Flex mt={"16px"}>
                  <Box>
                    <Text
                      lineHeight={"15px"}
                      m="0"
                      fontSize={{ base: "12px" }}
                      mb={"6px"}
                      fontWeight={400}
                      color={"var(--color-label-employee)"}
                    >
                      Đã có CCMG
                    </Text>
                    <Switch
                      {...register("isBrokerDebtCertificate")}
                      checked={checkedSwitch}
                      onChange={(e) => setCheckedSwitch(e.target.checked)}
                    />
                  </Box>
                </Flex>
              </Box>
              {/* Khooi 3 */}
              {Boolean(department) && (
                <Box mt={"20px"}>
                  <Text
                    fontSize={"16px"}
                    lineHeight={"19px"}
                    fontWeight={"600"}
                    color={"var(--color-header-employee)"}
                    mb={"16px"}
                  >
                    Phân loại nhân sự
                  </Text>
                  {/* 1 */}
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    gap={"27px"}
                    mb={"12px"}
                  >
                    <SelectControl
                      label="Loại nhân sự"
                      dataOption={businessCategorys}
                      selectedMain={watch("businessCategoryId")}
                      placeholder="Chọn loại nhân sự"
                      onChange={onchangeBusinessCategorys}
                      onFocus={() => handleFocus("businessCategoryId")}
                      onBlur={() => handleBlur("businessCategoryId")}
                      state={focusStates["businessCategoryId"]}
                      isSearchable={true}
                    />
                    <InputControl
                      label="Mã giới thiệu"
                      placeholder={
                        canUpdateReferralCode
                          ? "Nhập mã giới thiệu"
                          : "Không có quyền sửa người giới thiệu"
                      }
                      errors={errors.referralCodeId}
                      register={register("referralCodeId")}
                      onFocus={() => handleFocus("referralCodeId")}
                      onBlur={() => handleBlur("referralCodeId")}
                      state={focusStates["referralCodeId"]}
                      mwidth={"100%"}
                      disabled={!canUpdateReferralCode}
                    />
                  </Flex>
                  {/* 2 */}
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    gap={"27px"}
                    mb={"12px"}
                  >
                    <SelectControl
                      label="Loại bảo lãnh"
                      dataOption={guaranteeTypes}
                      placeholder="Chọn loại bảo lãnh"
                      selectedMain={watch("guaranteeTypeId")}
                      onChange={onchangeGuaranteeTypes}
                      onFocus={() => handleFocus("guaranteeTypeId")}
                      onBlur={() => handleBlur("guaranteeTypeId")}
                      state={focusStates["guaranteeTypeId"]}
                      isSearchable={true}
                    />
                    <FormControl
                      zIndex={zIndex === "datguaranteeDatee" ? 3 : 0}
                    >
                      <FormLabel
                        mb={"6px"}
                        fontSize={{ base: "12px" }}
                        fontWeight={400}
                        lineHeight={"15px"}
                        color={
                          focusStates["guaranteeDate"]
                            ? "var(--color-option-employee-hover)"
                            : "var(--color-label-employee)"
                        }
                      >
                        Ngày bảo lãnh
                      </FormLabel>
                      <InputGroup>
                        <DatePicker
                          width="100%"
                          selected={watch("guaranteeDate")}
                          onChange={(date) =>
                            handleDateChange(date, "guaranteeDate")
                          }
                          dateFormat="dd/MM/yyyy"
                          locale="vi"
                          placeholderText="dd/mm/yy"
                          className="custom-datepicker datePickerStyle"
                          onFocus={() =>
                            handleFocus("guaranteeDate", "datguaranteeDatee")
                          }
                          onBlur={() => handleBlur("guaranteeDate")}
                        />
                        <InputRightElement pointerEvents="none" h={"44px"}>
                          <Image src={calendarIcon} />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>
                  </Flex>
                  {/* 3 */}
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    gap={"27px"}
                  >
                    <FormControl
                      maxW={"259px"}
                      zIndex={zIndex === "depositDate" ? 3 : 0}
                    >
                      <FormLabel
                        mb={"6px"}
                        fontSize={{ base: "12px" }}
                        fontWeight={400}
                        lineHeight={"15px"}
                        color={
                          focusStates["depositDate"]
                            ? "var(--color-option-employee-hover)"
                            : "var(--color-label-employee)"
                        }
                      >
                        Ngày ký quỹ
                      </FormLabel>
                      <InputGroup>
                        <DatePicker
                          width="100%"
                          selected={watch("depositDate")}
                          onChange={(date) =>
                            handleDateChange(date, "depositDate")
                          }
                          dateFormat="dd/MM/yyyy"
                          locale="vi"
                          placeholderText="dd/mm/yy"
                          className="custom-datepicker datePickerStyle"
                          onFocus={() =>
                            handleFocus("depositDate", "depositDate")
                          }
                          onBlur={() => handleBlur("depositDate")}
                        />
                        <InputRightElement pointerEvents="none" h={"44px"}>
                          <Image src={calendarIcon} />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>
                  </Flex>
                </Box>
              )}
              {/* Khooi 4 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Thông tin liên hệ
                </Text>
                {/* 1 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    type="number"
                    label="Số điện thoại"
                    placeholder="Nhập số điện thoại"
                    errors={errors.phone}
                    register={register("phone")}
                    onFocus={() => handleFocus("phone")}
                    onBlur={() => handleBlur("phone")}
                    state={focusStates["phone"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                  <InputControl
                    type="email"
                    label="Email"
                    placeholder="Nhập email"
                    errors={errors.email}
                    register={register("email")}
                    onFocus={() => handleFocus("email")}
                    onBlur={() => handleBlur("email")}
                    state={focusStates["email"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                </Flex>
                {/* 2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                >
                  <InputControl
                    label="Chỗ ở hiện tại"
                    placeholder="Nhập chỗ ở hiện tại"
                    // errors={errors.currentAddress}
                    register={register("currentAddress")}
                    onFocus={() => handleFocus("currentAddress")}
                    onBlur={() => handleBlur("currentAddress")}
                    state={focusStates["currentAddress"]}
                    mwidth={"100%"}
                  />
                </Flex>
              </Box>
              {/* Khooi 5 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Thông tin giấy tờ
                </Text>
                {/* 1 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    type="number"
                    label="Số CCCD/CMND"
                    placeholder="Vui lòng nhập CCCD"
                    errors={errors.identificationNumber}
                    register={register("identificationNumber")}
                    onFocus={() => handleFocus("identificationNumber")}
                    onBlur={() => handleBlur("identificationNumber")}
                    state={focusStates["identificationNumber"]}
                    mwidth={"100%"}
                  />
                  <FormControl
                    w={"100%"}
                    maxW={"259px"}
                    zIndex={zIndex === "dateOfIdentification" ? 3 : 0}
                  >
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      lineHeight={"15px"}
                      fontWeight={400}
                      color={
                        focusStates["dateOfIdentification"]
                          ? "var(--color-option-employee-hover)"
                          : "var(--color-label-employee)"
                      }
                    >
                      Ngày cấp
                    </FormLabel>
                    <InputGroup>
                      <DatePicker
                        width="100%"
                        selected={watch("dateOfIdentification")}
                        onChange={(date) =>
                          handleDateChange(date, "dateOfIdentification")
                        }
                        dateFormat="dd/MM/yyyy"
                        locale="vi"
                        placeholderText="dd/mm/yy"
                        className="custom-datepicker datePickerStyle"
                        onFocus={() =>
                          handleFocus(
                            "dateOfIdentification",
                            "dateOfIdentification",
                          )
                        }
                        onBlur={() => handleBlur("dateOfIdentification")}
                      />
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    </InputGroup>
                    {errors.dateOfIdentification && (
                      <Text fontSize={{ base: "12px" }} color={"red"}>
                        {errors.dateOfIdentification.message}
                      </Text>
                    )}
                  </FormControl>
                </Flex>
                {/* 2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    label="Nơi cấp"
                    placeholder="Nhập nơi cấp..."
                    // errors={errors.placeOfIdentification}
                    register={register("placeOfIdentification")}
                    onFocus={() => handleFocus("placeOfIdentification")}
                    onBlur={() => handleBlur("placeOfIdentification")}
                    state={focusStates["placeOfIdentification"]}
                    onChange={(e) =>
                      setValue("placeOfIdentification", e.target.value)
                    }
                    data={DataPlaceOfIdentification}
                    mwidth={"100%"}
                  />
                </Flex>
                {/* 3 */}

                {/* Ảnh CCCD mặt trước  */}
                <Box flex={1} mt={"24px"}>
                  <Box mt={"16px"}>
                    <Flex>
                      <Box flex={"1"}>
                        <Flex alignItems="center" gap="4px">
                          <Text
                            color={"var(--color-label-employee)"}
                            fontSize={"12px"}
                          >
                            Ảnh CCCD mặt trước
                          </Text>
                        </Flex>
                        <Flex
                          mt={"8px"}
                          border={"0.5px solid var(--bg-line-employee)"}
                          borderRadius={"8px"}
                          justifyContent={"center"}
                          position={"relative"}
                        >
                          <Box
                            overflow={"hidden"}
                            w={"100%"}
                            h={"300px"}
                            display={"block"}
                            borderRadius={"11px"}
                            position={"relative"}
                          >
                            {idCardFrontImage && (
                              <Image
                                src={
                                  idCardFrontImage?.imageUrl
                                    ? idCardFrontImage?.imageUrl
                                    : `${baseURL}/${idCardFrontImage?.path}`
                                }
                                objectFit={"contain"}
                                h={"100%"}
                                w={"100%"}
                              />
                            )}
                          </Box>
                          {(idCardFrontImage?.path ||
                            idCardFrontImage?.imageUrl) && (
                            <Flex
                              position={"absolute"}
                              right={"8px"}
                              top={"8px"}
                              cursor={"pointer"}
                            >
                              <Box
                                position="absolute"
                                top="8px"
                                right="8px"
                                zIndex={2}
                              >
                                <Button
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => {
                                    setIdCardFrontImage(null);
                                    setValue("idCardFrontImage", null);
                                  }}
                                  padding={2}
                                >
                                  Xóa
                                </Button>
                              </Box>
                            </Flex>
                          )}

                          <Flex
                            position={"absolute"}
                            right={"8px"}
                            bottom={"8px"}
                            cursor={"pointer"}
                          >
                            <Box
                              padding={"8px"}
                              bg={"var(--fill-avatar)"}
                              borderRadius={"50%"}
                              mr={"8px"}
                              position={"relative"}
                            >
                              <Input
                                type="file"
                                accept="image/*"
                                multiple
                                opacity={0}
                                position="absolute"
                                top={0}
                                left={0}
                                width="100%"
                                height="100%"
                                cursor="pointer"
                                onChange={(e) =>
                                  changeIdCardFrontImage(
                                    e,
                                    1,
                                    idCardFrontImage?.id,
                                  )
                                }
                              />
                              <Image
                                src={imageIcon}
                                _hover={{
                                  filter: "var(--filter-red)",
                                }}
                              />
                            </Box>
                          </Flex>
                        </Flex>
                        {errors?.idCardFrontImage && (
                          <Text
                            fontSize={{ base: "12px" }}
                            color={"red"}
                            lineHeight={"15px"}
                            mt={"8px"}
                          >
                            {errors.idCardFrontImage.message}
                          </Text>
                        )}
                      </Box>
                    </Flex>

                    {idCardFrontImage?.file && (
                      <Flex w={"full"} justifyContent={"flex-end"} mt={2}>
                        <Button
                          bg={"var(--linear-gradient-employee)"}
                          color={"var(--color-main)"}
                          fontSize={"12px"}
                          lineHeight={"17px"}
                          // p={"6px 27px 5px 28px"}
                          fontWeight={"600"}
                          _hover={{
                            filter: "brightness(90%)",
                          }}
                          onClick={() =>
                            uploadIdCardFrontImage(idCardFrontImage?.file)
                          }
                          h={"28px"}
                          minH={"28px"}
                          w={"90px"}
                          isLoading={isLoadingUpload}
                        >
                          Tải lại
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </Box>

                {/* Ảnh CCCD mặt sau  */}
                <Box flex={1} mt={"24px"} mb={"24px"}>
                  <Box mt={"16px"}>
                    <Flex>
                      <Box flex={"1"}>
                        <Flex alignItems="center" gap="4px">
                          <Text
                            color={"var(--color-label-employee)"}
                            fontSize={"12px"}
                          >
                            Ảnh CCCD mặt sau
                          </Text>
                          {/* <Text
                                          fontSize={{ base: "12px" }}
                                          color={"red"}
                                          lineHeight={"15px"}
                                        >
                                          *
                                        </Text> */}
                        </Flex>
                        <Flex
                          mt={"8px"}
                          border={"0.5px solid var(--bg-line-employee)"}
                          borderRadius={"8px"}
                          justifyContent={"center"}
                          position={"relative"}
                        >
                          <Box
                            overflow={"hidden"}
                            w={"100%"}
                            h={"300px"}
                            display={"block"}
                            borderRadius={"11px"}
                            position={"relative"}
                          >
                            {idCardBackImage && (
                              <Image
                                src={
                                  idCardBackImage?.imageUrl
                                    ? idCardBackImage?.imageUrl
                                    : `${baseURL}/${idCardBackImage?.path}`
                                }
                                objectFit={"contain"}
                                h={"100%"}
                                w={"100%"}
                              />
                            )}
                          </Box>
                          {(idCardBackImage?.path ||
                            idCardBackImage?.imageUrl) && (
                            <Flex
                              position={"absolute"}
                              right={"8px"}
                              top={"8px"}
                              cursor={"pointer"}
                            >
                              <Box
                                position="absolute"
                                top="8px"
                                right="8px"
                                zIndex={2}
                              >
                                <Button
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => {
                                    setIdCardBackImage(null);
                                    setValue("idCardBackImage", null);
                                  }}
                                  padding={2}
                                >
                                  Xóa
                                </Button>
                              </Box>
                            </Flex>
                          )}

                          <Flex
                            position={"absolute"}
                            right={"8px"}
                            bottom={"8px"}
                            cursor={"pointer"}
                          >
                            <Box
                              padding={"8px"}
                              bg={"var(--fill-avatar)"}
                              borderRadius={"50%"}
                              mr={"8px"}
                              position={"relative"}
                            >
                              <Input
                                type="file"
                                accept="image/*"
                                multiple
                                opacity={0}
                                position="absolute"
                                top={0}
                                left={0}
                                width="100%"
                                height="100%"
                                cursor="pointer"
                                onChange={(e) =>
                                  changeIdCardBackImage(
                                    e,
                                    2,
                                    idCardBackImage?.id,
                                  )
                                }
                              />
                              <Image
                                src={imageIcon}
                                _hover={{
                                  filter: "var(--filter-red)",
                                }}
                              />
                            </Box>
                          </Flex>
                        </Flex>
                        {errors?.idCardBackImage && (
                          <Text
                            fontSize={{ base: "12px" }}
                            color={"red"}
                            lineHeight={"15px"}
                            mt={"8px"}
                          >
                            {errors.idCardBackImage.message}
                          </Text>
                        )}
                      </Box>
                    </Flex>

                    {idCardBackImage?.file && (
                      <Flex w={"full"} justifyContent={"flex-end"} mt={2}>
                        <Button
                          bg={"var(--linear-gradient-employee)"}
                          color={"var(--color-main)"}
                          fontSize={"12px"}
                          lineHeight={"17px"}
                          // p={"6px 27px 5px 28px"}
                          fontWeight={"600"}
                          _hover={{
                            filter: "brightness(90%)",
                          }}
                          onClick={() =>
                            uploadIdCardBackImage(idCardBackImage?.file)
                          }
                          h={"28px"}
                          minH={"28px"}
                          w={"90px"}
                          isLoading={isLoadingUpload}
                        >
                          Tải lại
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </Box>

                {/* Ảnh VNeID mặt trước / mặt sau (không bắt buộc) */}
                <VneidImageField
                  label="Ảnh VNeID mặt trước"
                  name="vneidFrontImage"
                  defaultPath={defaultValues?.vneidFrontImage}
                  isOpen={isOpen}
                  setValue={setValue}
                />
                <VneidImageField
                  label="Ảnh VNeID mặt sau"
                  name="vneidBackImage"
                  defaultPath={defaultValues?.vneidBackImage}
                  isOpen={isOpen}
                  setValue={setValue}
                />

                {/* 4 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                >
                  <InputControl
                    label="Địa chỉ thường trú"
                    placeholder="Nhập địa chỉ thường trú..."
                    // errors={errors.permanentAddress}
                    register={register("permanentAddress")}
                    onFocus={() => handleFocus("permanentAddress")}
                    onBlur={() => handleBlur("permanentAddress")}
                    state={focusStates["permanentAddress"]}
                    mwidth={"100%"}
                  />
                  <InputControl
                    label="Mã số thuế"
                    placeholder="Nhập mã số thuế"
                    register={register("taxCode")}
                    onFocus={() => handleFocus("taxCode")}
                    onBlur={() => handleBlur("taxCode")}
                    state={focusStates["taxCode"]}
                    // mwidth={"100%"}
                  />
                </Flex>
              </Box>
              {/* Khooi 6 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Thông tin thanh toán
                </Text>
                {/* 1 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                >
                  <InputControl
                    type="number"
                    label="Số tài khoản"
                    placeholder="Nhập số tài khoản"
                    errors={errors.bankAccount}
                    register={register("bankAccount")}
                    onFocus={() => handleFocus("bankAccount")}
                    onBlur={() => handleBlur("bankAccount")}
                    state={focusStates["bankAccount"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                  <InputControl
                    label="Tên ngân hàng"
                    placeholder="Nhập tên ngân hàng"
                    // errors={errors.bankName}
                    register={register("bankName")}
                    onFocus={() => handleFocus("bankName")}
                    onBlur={() => handleBlur("bankName")}
                    state={focusStates["bankName"]}
                    mwidth={"100%"}
                  />
                  {/* <FormControl>
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      fontWeight={400}
                      color={
                        focusStates["bankAccount"]
                          ? "var(--color-option-employee-hover)"
                          : "var(--color-label-employee)"
                      }
                    >
                      Số tài khoản
                    </FormLabel>
                    <Input
                      type="number"
                      maxW={"259px"}
                      h={"44px"}
                      fontSize={{ base: "14px" }}
                      fontWeight={"500"}
                      padding={"13px 12px 14px 12px"}
                      borderRadius={"8px"}
                      border={"0.5px solid var(--bg-line-employee)"}
                      placeholder="Nhập số tài khoản NH..."
                      sx={{
                        "&::placeholder": {
                          color: "var(--color-placeHolder-employee)",
                        },
                      }}
                      _focus={{
                        boxShadow: "none",
                        borderColor: "var(--color-option-employee-hover)",
                      }}
                      value={watch("bankAccount") || ""}
                      {...register("bankAccount")}
                      onChange={(e) => setValue("bankAccount", e.target.value)}
                      onFocus={() => handleFocus("bankAccount")}
                      onBlur={() => handleBlur("bankAccount")}
                    />
                    {errors.bankAccount && (
                      <Text fontSize={{ base: "12px" }} color={"red"}>
                        {errors.bankAccount.message}
                      </Text>
                    )}
                  </FormControl> */}
                  {/* <FormControl>
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      fontWeight={400}
                      color={
                        focusStates["bankName"]
                          ? "var(--color-option-employee-hover)"
                          : "var(--color-label-employee)"
                      }
                    >
                      Tên ngân hàng
                    </FormLabel>
                    <Input
                      maxW={"259px"}
                      h={"44px"}
                      fontSize={{ base: "14px" }}
                      fontWeight={"500"}
                      padding={"13px 12px 14px 12px"}
                      borderRadius={"8px"}
                      border={"0.5px solid var(--bg-line-employee)"}
                      placeholder="Nhập tên ngân hàng"
                      value={watch("bankName") || ""}
                      onChange={(e) => setValue("bankName", e.target.value)}
                      sx={{
                        "&::placeholder": {
                          color: "var(--color-placeHolder-employee)",
                        },
                      }}
                      _focus={{
                        boxShadow: "none",
                        borderColor: "var(--color-option-employee-hover)",
                      }}
                      {...register("bankName")}
                      onFocus={() => handleFocus("bankName")}
                      onBlur={() => handleBlur("bankName")}
                    />
                    {errors.bankName && (
                      <Text fontSize={{ base: "12px" }} color={"red"}>
                        {errors.bankName.message}
                      </Text>
                    )}
                  </FormControl> */}
                </Flex>
              </Box>
            </Box>
            <Flex
              justifyContent={"flex-end"}
              mt={"24px"}
              w={"100%"}
              // maxW={"545px"}
            >
              <Button
                type="submit"
                borderRadius={"8px"}
                background={"var(--linear-gradient-employee)"}
                color={"var(--color-main)"}
                w={"164px"}
                h={"44px"}
                _hover={{
                  filter: "brightness(90%)",
                }}
              >
                {defaultValues ? "Lưu thông tin" : "Thêm nhân viên"}
              </Button>
            </Flex>
          </form>
        </ModalContent>
      </Modal>
    );
  },
);

export default FormEmployee;
